import Swal from "sweetalert2";
import { truncateMiddle } from "@/utils/fileHelpers.js";

/**
 * Clicking a header sorts by it ascending; clicking the active header flips direction.
 *
 * Returns the new { sort, direction } rather than mutating, so the caller owns the state and can
 * put it straight into a PATCH_QUERY commit.
 */
export function toggleSort(current, column) {
  if (current.sort !== column) {
    return { sort: column, direction: "asc" };
  }

  return {
    sort: column,
    direction: current.direction === "asc" ? "desc" : "asc",
  };
}

/**
 * The share button's label follows the selection, so there is only ever one button and never a
 * doubt about what is going to be shared.
 */
export function shareButtonLabel(selectedCount) {
  if (!selectedCount) {
    return "Share folder";
  }

  return selectedCount === 1
    ? "Share 1 document"
    : `Share ${selectedCount} documents`;
}

/**
 * The delete confirmation names the consequence, instead of "You won't be able to revert this!"
 * — which is only true when the subject owns the folder.
 *
 * `isOwnList` distinguishes the viewer deleting from their OWN Documents tab from staff deleting
 * from someone ELSE's (e.g. an Admin on a client's tab) — "your list" was said unconditionally,
 * which is wrong the second way round.
 */
export function deleteFolderMessage(folder, isOwnList = true) {
  if (folder.is_shared_with_subject) {
    const owner = (folder.owner && folder.owner.name) || "its owner";
    const whose = isOwnList ? "your list" : "this list";

    return `This removes it from ${whose}. ${owner} and everyone else keep it.`;
  }

  const count = folder.documents_count || 0;
  const subfolders = folder.subfolder_count || 0;
  const people =
    (folder.audience_summary && folder.audience_summary.count) || 0;

  const documentsText = count === 1 ? "1 document" : `${count} documents`;

  // Both counts are of what sits directly in the folder, so the folder's own files have to be
  // named too, and the depth below is described in words rather than as a number it is not.
  const below =
    subfolders === 1
      ? "1 subfolder and everything below it"
      : `${subfolders} subfolders and everything below them`;
  const what = subfolders
    ? `This deletes the folder and everything inside it — ${
        count ? `${documentsText} here, plus ${below}` : below
      }`
    : `${documentsText} will be deleted`;

  if (!people) {
    return `${what}. This cannot be undone.`;
  }

  const peopleText =
    people === 1
      ? "1 person will lose access"
      : `${people} people will lose access`;

  return `${what}, and ${peopleText}. This cannot be undone.`;
}

/** Today's delete-confirmation dialog styling, unchanged — only the words vary per call. */
export function confirmDialog(Swal) {
  return Swal.mixin({
    customClass: {
      confirmButton: "btn btn-black ml-4",
      cancelButton: "btn btn-danger",
    },
    buttonsStyling: false,
  });
}

/**
 * How long a search box waits before hitting the API — matches `ConversationList.applySearch`.
 * Consumers use lodash's `debounce` for its `.cancel()`, needed so a pending search doesn't fire
 * after the component unmounts or the subject changes.
 */
export const SEARCH_DEBOUNCE_MS = 500;

/** The share-preview typeahead is not a list search; it stays snappier. */
export const PREVIEW_DEBOUNCE_MS = 300;

const ROLE_ADMIN = 1;
const ROLE_PROVIDER = 2;
const ROLE_CLIENT = 3;
const ROLE_MANAGER = 5;

/** The product's nouns are tenant-configurable, so never hardcode "client" or "provider". */
export function roleLabel(vm, roleId) {
  if (roleId === ROLE_CLIENT) {
    return vm.$customerLabel("singular", true);
  }
  if (roleId === ROLE_PROVIDER) {
    return vm.$providerLabel("singular", true);
  }

  return roleId === ROLE_ADMIN || roleId === ROLE_MANAGER
    ? "Administrator"
    : "Staff";
}

/** Which configurable permission gates acting on a resource owned by someone of this role. */
function ownerPermission(ownerRole) {
  if (ownerRole === ROLE_CLIENT) {
    return "clients";
  }

  return ownerRole === ROLE_PROVIDER ? "providers" : null;
}

/**
 * Which folder controls this viewer gets. Mirrors what DocumentAccess enforces server-side;
 * the API refuses anything this lets through, so being wrong here is cosmetic, not a hole.
 */
export function folderPermissions(vm, folder, subjectUserId = null) {
  if (vm.$isClient()) {
    return {
      delete: false,
      share: false,
      move: false,
      rename: false,
      upload: false,
      pin: true,
    };
  }

  const viewerId = vm.$store.state.auth.user && vm.$store.state.auth.user.id;
  const isOwnTab = !subjectUserId || Number(subjectUserId) === Number(viewerId);

  // Reaching this tab by sharing means delete detaches the share row rather than the folder.
  const sharedOntoTab = !!folder.is_shared_with_subject;
  const isOwn = !!(folder.owner && folder.owner.is_you);

  if (vm.$isProvider()) {
    // Sharing, moving and renaming are all edits on the folder itself.
    const edit = vm.$can("folder", "edit");

    return {
      // A shared folder is detached, not destroyed — and only off your own tab.
      delete: sharedOntoTab ? isOwnTab : isOwn && vm.$can("folder", "delete"),
      share: edit,
      move: edit,
      rename: edit,
      // Upload keys on the TAB, not the folder: store() gates on the owner_id sent with it.
      upload: isOwnTab ? vm.$can("document", "create") : true,
      pin: true,
    };
  }

  const permission = ownerPermission(folder.owner && folder.owner.role);
  const allowed = isOwn || !permission || vm.$can(permission, "edit");

  return {
    delete: sharedOntoTab ? isOwnTab || allowed : allowed,
    share: allowed,
    move: allowed,
    rename: allowed,
    upload: allowed,
    pin: true,
  };
}

/** File types the in-app viewer can render. Carried over from the previous screens verbatim. */
const VIEWABLE = ["image", "pdf", "excel", "word", "times", "video", "dicom"];

export function isViewable(doc) {
  return VIEWABLE.includes(doc && doc.type);
}

/** The API decides this — the rule needs the folder's owner, which the browser never gets. */
export function canDeleteDocument(doc) {
  return !!doc.can_delete;
}

/** Matches the `folders.name` column, which the create and rename endpoints reject past with a 422. */
export const FOLDER_NAME_MAX_LENGTH = 255;

/** The name box shared by the "New folder" and "Rename" prompts. */
const folderNameInput = {
  input: "text",
  inputLabel: "Folder name",
  showCancelButton: true,
  inputAttributes: { maxlength: FOLDER_NAME_MAX_LENGTH },
  inputValidator: (value) => {
    const name = value ? value.trim() : "";
    if (!name) {
      return "Give the folder a name";
    }
    // Unreachable while `maxlength` holds; it is the guard if that attribute is ever dropped.
    return name.length > FOLDER_NAME_MAX_LENGTH
      ? `Folder name must be ${FOLDER_NAME_MAX_LENGTH} characters or fewer`
      : undefined;
  },
};

/**
 * The "New folder" prompt, used by the top-level list and by a folder's own contents — the only
 * difference between them is whether a parent is passed.
 */
export function promptNewFolder(
  vm,
  { subjectUserId, parentId = null, parent = null, onCreated }
) {
  // Created inside a folder, it is visible to everyone that folder reaches — which is not
  // obvious when the folder is one that was shared with you. The move dialog says the same
  // thing about a destination; creating changes it just as much.
  const audience = parent && parent.audience_summary;
  const inherits =
    audience && audience.count
      ? `Everyone who can see "${truncateMiddle(parent.name, 40)}" (${
          audience.label
        }) will be able to see this folder.`
      : "";

  Swal.fire({
    ...folderNameInput,
    title: parentId ? "New subfolder" : "New folder",
    text: inherits || undefined,
    confirmButtonText: "Create",
  }).then((result) => {
    if (!result.isConfirmed) {
      return;
    }

    vm.$store
      .dispatch("folders/save", {
        name: result.value.trim(),
        user_id: subjectUserId,
        folder_id: parentId || undefined,
      })
      .then(onCreated)
      .catch((err) => {
        if (!err || !err.accessDenied) {
          // `err` guarded before `err.data` — the interceptor rejects with `undefined` on a
          // network failure, and reading `.data` off that threw instead of showing this.
          Swal.fire(
            "Error",
            (((err && err.data) || {}).error || {}).message ||
              "Something went wrong!",
            "error"
          );
        }
      });
  });
}

/** The "Rename" prompt, opened from a folder's row with its current name filled in. */
export function promptRenameFolder(vm, { folder, onRenamed }) {
  Swal.fire({
    ...folderNameInput,
    title: "Rename folder",
    inputValue: folder.name,
    confirmButtonText: "Save",
  }).then((result) => {
    const name = result.isConfirmed ? result.value.trim() : "";
    if (!name || name === folder.name) {
      return;
    }

    vm.$store
      .dispatch("folders/rename", { folderId: folder.id, name })
      .then((renamed) => renamed && onRenamed());
  });
}
